// @ts-ignore
// core version + navigation, pagination modules:
import Swiper from 'swiper';
import { Keyboard, Mousewheel } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
// import Swiper and modules styles
import 'swiper/css';
import 'swiper/css/autoplay';

import $ from 'cash-dom';
import {subscribe, cLog, onReady} from '@js/humans/helper';

export default class Slideshow {
    /**
     * Singleton instance of the component
     */
    static #instance: Slideshow;

    protected swipers: Swiper[] = [];

    /**
     * UI references
     */
    protected ui = {
        slideshows: '.js-slideshow',
        lazySlideshows: '.js-slideshow-lazy',
    };

    /**
     * Constructor
     */
    protected constructor() {
        this.registerEvents();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): Slideshow {
        if (!Slideshow.#instance) {
            Slideshow.#instance = new Slideshow();
        }

        return Slideshow.#instance;
    }

    /**
     * Register required events
     */
    protected registerEvents(): void {
        // Initialize slideshows when DOM is ready
        onReady(() => {
            cLog('Slideshow: Initializing on page load');
            this.initSwipers(this.ui.slideshows);

            // Also initialize lazy slideshows that are in pre-expanded repository items
            const expandedArticles = document.querySelectorAll('.js-repo-article.is-expanded');
            expandedArticles.forEach((article) => {
                cLog('Slideshow: Initializing lazy slideshows in pre-expanded article', article);
                this.initSwipers(this.ui.lazySlideshows, article as HTMLElement);
            });
        });

        // Initialize slideshows when modal content is loaded
        subscribe('modal.content-loaded', (_topic, payload: { $modal: any }) => {
            cLog('Slideshow: modal.content-loaded event received', payload);
            const modal = payload.$modal?.get ? payload.$modal.get(0) : payload.$modal;
            this.initSwipers(this.ui.slideshows, modal);
        });

        // Initialize lazy slideshows when repository item is expanded
        subscribe('repository.expanded', (_topic, payload: { $article: any }) => {
            cLog('Slideshow: repository.expanded event received', payload);
            const article = payload.$article?.get ? payload.$article.get(0) : payload.$article;
            this.initSwipers(this.ui.lazySlideshows, article);
        });
    }

    /**
     * Initialize all slideshows within the given context
     *
     * @param selector CSS selector for the slideshow elements
     * @param context Optional root DOM node to scope the search (default: document)
     */
    protected initSwipers(selector: string, context: HTMLElement | Document = document): void {
        const $slideshows = $(selector, context);
        const contextName = context === document ? 'document' : (context as HTMLElement).id || (context as HTMLElement).className || 'unknown';

        cLog(`Slideshow: Found ${$slideshows.length} slideshow(s) in context:`, contextName);

        $slideshows.each((_index: number, element: HTMLElement) => {
            const $element = $(element);

            // Skip if not a valid element
            if (!(element instanceof HTMLElement)) {
                cLog('Slideshow: Skipping invalid element', element);
                return;
            }

            // Skip if already initialized
            if ($element.data('swiper-instance')) {
                cLog('Slideshow: Skipping already initialized slideshow', element);
                return;
            }

            // Skip if element is not visible (e.g., inside collapsed accordion)
            // This prevents errors when trying to initialize hidden elements
            if (element.offsetParent === null && !element.closest('.is-expanded')) {
                cLog('Slideshow: Skipping hidden element (not in expanded container)', element);
                return;
            }

            // Define swiper type based on [data-swiper-type]
            const swiperType = $element.data('swiper-type') || 'default';

            // Get swiper options
            const swiperParams = this.getSwiperParams(swiperType);

            // Init swiper
            const swiper = new Swiper(element, swiperParams);
            this.swipers.push(swiper);

            // Store instance on element to prevent re-initialization
            $element.data('swiper-instance', swiper);

            cLog('Slideshow: Successfully initialized slideshow', element);
        });
    }

    /**
     * Get Swiper parameters based on swiper type
     * @param _swiperType - Value of data-swiper-type attribute (reserved for future use)
     * @returns SwiperOptions
     */
    protected getSwiperParams(_swiperType: string | undefined): SwiperOptions {
        // Default parameters
        let params: SwiperOptions = {
            // Docs: https://swiperjs.com/swiper-api

            // Install modules
            modules: [Keyboard, Mousewheel],

			// loop: true,
            lazyPreloadPrevNext: 2,

            slidesPerView: "auto",
            // spaceBetween: 10,

            keyboard: {
                enabled: true,
            },

            // Turn on mousewheel control
            mousewheel: {
                enabled: true,
                forceToAxis: true,
            },

            grabCursor: true,
            touchEventsTarget: 'container',
        };

        return params;
    }
}
